import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { onboardingService } from '../services/onboardingService';

interface VisionCache {
  hasVision: boolean;
  timestamp: number;
}

interface UseVisionCheckOptions {
  onNoVision?: () => void;
  skipRedirect?: boolean;
}

export const useVisionCheck = (options: UseVisionCheckOptions = {}) => {
  const { isAuthenticated, userId } = useAuth();
  const [hasVision, setHasVision] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getVisionCache = useCallback((userId: string): VisionCache | null => {
    try {
      const cached = localStorage.getItem(`vision_${userId}`);
      return cached ? JSON.parse(cached) : null;
    } catch (error) {
      console.error("Error reading vision cache from localStorage:", error);
      return null;
    }
  }, []);

  const setVisionCache = useCallback((userId: string, hasVision: boolean) => {
    try {
      localStorage.setItem(
        `vision_${userId}`,
        JSON.stringify({
          hasVision,
          timestamp: Date.now(),
        })
      );
    } catch (error) {
      console.error("Error writing vision cache to localStorage:", error);
    }
  }, []);

  const clearVisionCache = useCallback((userId: string) => {
    try {
      localStorage.removeItem(`vision_${userId}`);
    } catch (error) {
      console.error("Error clearing vision cache from localStorage:", error);
    }
  }, []);

  const checkVision = useCallback(async () => {
    console.log('[VisionCheck] Starting. isAuthenticated:', isAuthenticated, 'userId:', userId);
    if (!isAuthenticated || !userId) {
      console.log('[VisionCheck] Not authenticated or no userId, skipping');
      setIsChecking(false);
      return;
    }

    const userIdStr = String(userId);

    const cached = getVisionCache(userIdStr);
    console.log('[VisionCheck] Cache for user', userIdStr, ':', cached);

    const isCacheValid =
      cached && Date.now() - cached.timestamp < 24 * 60 * 60 * 1000;

    if (isCacheValid) {
      console.log('[VisionCheck] Using valid cache. hasVision:', cached.hasVision);
      setHasVision(cached.hasVision);
      setIsChecking(false);

      if (!cached.hasVision && options.onNoVision) {
        options.onNoVision();
      }
      return;
    }

    console.log('[VisionCheck] No valid cache, calling backend API...');
    try {
      const profile = await onboardingService.getUserProfile(userId);
      console.log('[VisionCheck] Backend response:', JSON.stringify(profile));

      const hasVisionValue = !!(
        profile.threeYearSummary && profile.threeYearSummary.trim() !== ""
      );
      console.log('[VisionCheck] hasVision result:', hasVisionValue, 'threeYearSummary:', profile.threeYearSummary?.substring(0, 50));
      setVisionCache(userIdStr, hasVisionValue);
      setHasVision(hasVisionValue);
      setError(null);

      if (!hasVisionValue && options.onNoVision) {
        options.onNoVision();
      }
    } catch (error) {
      console.error("[VisionCheck] ERROR calling backend:", error);
      setError(error instanceof Error ? error.message : 'Unknown error');
      setHasVision(false);
      clearVisionCache(userIdStr);

      if (options.onNoVision) {
        options.onNoVision();
      }
    } finally {
      setIsChecking(false);
    }
  }, [
    isAuthenticated,
    userId,
    getVisionCache,
    setVisionCache,
    clearVisionCache,
    options.onNoVision
  ]);

  useEffect(() => {
    if (!isAuthenticated || !userId) {
      setIsChecking(false);
      return;
    }
    
    setIsChecking(true);
    checkVision();
  }, [isAuthenticated, userId, checkVision]);

  return {
    hasVision,
    isChecking,
    error,
    checkVision,
    clearVisionCache: () => userId ? clearVisionCache(String(userId)) : undefined
  };
};
